// Money handling. Backend stores integer minor units (paise, cents) and
// attaches a currency_config to each cart/order. The UI must never format
// or compare money without going through these helpers.

const FALLBACK_CONFIG = Object.freeze({ code: 'INR', symbol: '₹', precision: 2 });

/** @param {{ precision?: number }} cfg */
function unit(cfg) {
  return 10 ** (cfg?.precision ?? FALLBACK_CONFIG.precision);
}

/**
 * Convert minor units → major (paise → rupees) as a plain number.
 * @param {number} minor
 * @param {import('@core/types').CurrencyConfig} [config]
 */
export function fromMinor(minor, config) {
  if (minor == null || Number.isNaN(minor)) return 0;
  return minor / unit(config);
}

/**
 * Convert major number → minor units, floored to integer.
 * @param {number} major
 * @param {import('@core/types').CurrencyConfig} [config]
 */
export function toMinor(major, config) {
  if (major == null || Number.isNaN(major)) return 0;
  return Math.round(major * unit(config));
}

function formatNumber(value, precision, locale) {
  try {
    return new Intl.NumberFormat(locale, {
      minimumFractionDigits: precision,
      maximumFractionDigits: precision,
    }).format(value);
  } catch {
    return value.toFixed(precision);
  }
}

/**
 * Format a minor-unit amount for display. When the account preference provides
 * a symbol, render that exact symbol everywhere instead of relying on Intl.
 * @param {number} minor
 * @param {import('@core/types').CurrencyConfig} [config]
 * @param {{ locale?: string, withSymbol?: boolean }} [opts]
 */
export function formatMoney(minor, config, opts = {}) {
  const cfg = config ?? FALLBACK_CONFIG;
  const locale = opts.locale ?? 'en-IN';
  const value = fromMinor(minor, cfg);
  const precision = cfg.precision ?? FALLBACK_CONFIG.precision;
  const number = formatNumber(value, precision, locale);

  if (opts.withSymbol === false) return number;
  if (cfg.symbol) return `${cfg.symbol}${number}`;

  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: cfg.code || FALLBACK_CONFIG.code,
      minimumFractionDigits: precision,
      maximumFractionDigits: precision,
    }).format(value);
  } catch {
    const fixed = value.toFixed(precision);
    return opts.withSymbol === false ? fixed : `${cfg.symbol ?? ''}${fixed}`;
  }
}
